<template>
  <div class="business-select-container" ref="container">
    <label v-if="label" class="business-select-label">{{ label }}</label>
    <div class="business-select-input" :class="inputClass" @click="openDropdown" ref="inputWrapper">
      <!-- Selected business display -->
      <div v-if="selectedBusiness" class="selected-business">
        <img src="/icons/business.png" alt="Business Icon" class="business-icon">
        <span class="business-name">
          {{ selectedBusiness.name || formatPhone(selectedBusiness.faxNumber) }}
          <span v-if="selectedBusiness.name && selectedBusiness.faxNumber" class="business-fax-number">
            {{ formatPhone(selectedBusiness.faxNumber) }}
          </span>
        </span>
        <button type="button" class="clear-button" @click.stop="clearSelection">
          <vue-feather type="x" size="16" />
        </button>
      </div>
      <!-- Input field when no business selected -->
      <input v-else ref="input" type="text" class="business-select-field" :placeholder="computedPlaceholder"
        :value="query" :disabled="disabled" :inputmode="inputMode" :maxlength="maxLength" autocomplete="nope"
        data-lpignore="true" data-form-type="other" @input="handleInput" @focus="handleFocus" @blur="handleBlur"
        @keydown="handleKeydown" @paste="handlePaste">
    </div>
    <span v-if="error" class="business-select-error">{{ error }}</span>

    <!-- Dropdown -->
    <FloatingDropdown :open="isOpen && !selectedBusiness" :trigger-ref="$refs.inputWrapper" @dismiss="close">
      <div class="business-dropdown" ref="dropdown">
        <div class="business-list" @scroll="handleScroll">
          <!-- Business results -->
          <div v-for="(business, index) of businesses" :key="business.id" class="business-item"
            :class="{ 'business-item--highlighted': highlightedIndex === index }"
            @mousedown.prevent="selectBusiness(business)" @mouseenter="highlightedIndex = index">
            <img src="/icons/business.png" alt="Business Icon" class="business-item-icon">
            <div class="business-item-info">
              <span class="business-item-name">{{ business.name || formatPhone(business.faxNumber) }}</span>
            </div>
            <div class="business-item-contact" v-if="business.faxNumber && business.name">
              <img src="@/assets/icons/fax.png" alt="Fax Icon" class="contact-icon">
              {{ formatPhone(business.faxNumber) }}
            </div>
          </div>

          <!-- Loading indicator -->
          <div class="loading-status" v-if="loading">
            <img src="/loading.svg" class="loading-icon">
          </div>

          <!-- Create new contact option -->
          <div v-if="showCreateOption && doneLoading" class="business-item create-contact-item"
            :class="{ 'business-item--highlighted': highlightedIndex === businesses.length }"
            @mousedown.prevent="handleCreateContact" @mouseenter="highlightedIndex = businesses.length">
            <div class="create-contact-icon">
              <vue-feather type="plus" size="16" />
            </div>
            <div class="business-item-info">
              <span class="business-item-name">Create new contact</span>
            </div>
          </div>
        </div>
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'
import * as coreApi from '../../api/core.js'
import * as commonUtils from '../../utils/common.js'

export default {
  name: 'BusinessSelectInput',
  components: { VueFeather, FloatingDropdown },
  props: {
    // v-model for the selected business object
    modelValue: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Search by name or fax number...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    showCreateOption: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      query: '',
      isOpen: false,
      isFocused: false,
      loading: false,
      businesses: [],
      doneLoading: false,
      searchTimeout: null,
      highlightedIndex: 0
    }
  },
  computed: {
    selectedBusiness() {
      return this.modelValue
    },
    inputClass() {
      return {
        'business-select-input--focused': this.isFocused,
        'business-select-input--disabled': this.disabled,
        'business-select-input--error': this.error,
        'business-select-input--selected': this.selectedBusiness
      }
    },
    detectedFormat() {
      const digitsOnly = this.query.replace(/\D/g, '')
      if (digitsOnly.length >= 3) return 'phone'
      return 'text'
    },
    computedPlaceholder() {
      return this.placeholder
    },
    inputMode() {
      if (this.detectedFormat === 'phone') return 'numeric'
      return 'text'
    },
    maxLength() {
      if (this.detectedFormat === 'phone') return 14
      return undefined
    },
    // Check if query is a valid 10-digit phone number
    hasValidPhoneNumber() {
      const digitsOnly = this.query.replace(/\D/g, '')
      return digitsOnly.length === 10
    }
  },
  watch: {
    query() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.searchBusinesses()
      }, 300)
      // Emit validity change for parent components
      this.$emit('validityChange', this.hasValidPhoneNumber)
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    // Preload initial list
    this.searchBusinesses()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    clearTimeout(this.searchTimeout)
  },
  methods: {
    formatPhone(phoneStr) {
      return commonUtils.formatPhone(phoneStr)
    },

    openDropdown() {
      if (this.disabled) return
      // If a business is selected, clear it to allow changing selection
      if (this.selectedBusiness) {
        this.$emit('update:modelValue', null)
        this.$emit('clear')
      }
      this.isOpen = true
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    },

    handleFocus() {
      this.isFocused = true
      this.isOpen = true
      if (this.businesses.length === 0 && !this.loading) {
        this.searchBusinesses()
      }
    },

    handleBlur() {
      this.isFocused = false
      // Delay closing to allow click events on dropdown items
      setTimeout(() => {
        if (!this.isFocused) {
          this.isOpen = false
        }
      }, 150)
    },

    handleClickOutside(event) {
      const clickedDropdown = this.$refs.dropdown && this.$refs.dropdown.contains(event.target)
      const clickedContainer = this.$refs.container && this.$refs.container.contains(event.target)
      if (!clickedDropdown && !clickedContainer) {
        this.isOpen = false
      }
    },

    handleInput(event) {
      const input = event.target
      const oldValue = this.query || ''
      let newValue = input.value
      let cursorPos = input.selectionStart

      if (this.detectedFormat === 'phone') {
        const result = this.formatPhoneInput(newValue, oldValue, cursorPos)
        newValue = result.value
        cursorPos = result.cursor
      }

      this.query = newValue
      this.highlightedIndex = 0

      if (this.detectedFormat === 'phone') {
        this.$nextTick(() => {
          input.setSelectionRange(cursorPos, cursorPos)
        })
      }
    },

    handlePaste(event) {
      if (this.detectedFormat === 'phone') {
        event.preventDefault()
        const paste = (event.clipboardData || window.clipboardData).getData('text')
        const digitsOnly = paste.replace(/\D/g, '')

        const input = this.$refs.input
        const start = input.selectionStart
        const end = input.selectionEnd
        const currentValue = this.query || ''

        const newValue = currentValue.slice(0, start) + digitsOnly + currentValue.slice(end)
        const result = this.formatPhoneInput(newValue, currentValue, start + digitsOnly.length)
        this.query = result.value
        this.$nextTick(() => {
          input.setSelectionRange(result.cursor, result.cursor)
        })
      }
    },

    handleKeydown(event) {
      // Handle phone format restrictions
      if (this.detectedFormat === 'phone') {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End']
        const isDigit = /^\d$/.test(event.key)
        const isAllowedKey = allowedKeys.includes(event.key)
        const hasModifier = event.ctrlKey || event.metaKey || event.altKey

        if (!isDigit && !isAllowedKey && !hasModifier) {
          event.preventDefault()
          return
        }
      }

      // Navigation in dropdown
      const totalItems = this.businesses.length + (this.showCreateOption && this.doneLoading ? 1 : 0)

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, totalItems - 1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (this.highlightedIndex < this.businesses.length) {
          this.selectBusiness(this.businesses[this.highlightedIndex])
        } else if (this.showCreateOption && this.highlightedIndex === this.businesses.length) {
          this.handleCreateContact()
        }
      } else if (event.key === 'Escape') {
        this.isOpen = false
        this.$refs.input?.blur()
      }

      // Handle backspace for phone format
      if (this.detectedFormat === 'phone' && event.key === 'Backspace') {
        const input = this.$refs.input
        const pos = input.selectionStart
        const value = this.query || ''

        if (pos > 0 && /\D/.test(value[pos - 1])) {
          event.preventDefault()
          let newPos = pos - 1
          while (newPos > 0 && /\D/.test(value[newPos - 1])) {
            newPos--
          }
          if (newPos > 0) {
            const newValue = value.slice(0, newPos - 1) + value.slice(pos)
            this.query = this.formatPhoneInput(newValue, value, newPos - 1).value
            this.$nextTick(() => {
              input.setSelectionRange(newPos - 1, newPos - 1)
            })
          }
        }
      }
    },

    formatPhoneInput(value, _oldValue, cursorPos) {
      let digits = value.replace(/\D/g, '')
      if (digits.startsWith('1') && digits.length === 11) {
        digits = digits.substring(1)
      }
      if (digits.length > 10) {
        digits = digits.slice(0, 10)
      }

      const rawBeforeCursor = value.slice(0, cursorPos)
      const digitsBeforeCursor = Math.min(rawBeforeCursor.replace(/\D/g, '').length, 10)

      let formatted = ''
      if (digits.length > 0) {
        formatted = '('
        formatted += digits.slice(0, 3)
        if (digits.length >= 3) {
          formatted += ') '
          formatted += digits.slice(3, 6)
          if (digits.length >= 6) {
            formatted += '-'
            formatted += digits.slice(6, 10)
          }
        }
      }

      let newCursor = 0
      let digitCount = 0
      for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
        newCursor = i + 1
        if (/\d/.test(formatted[i])) {
          digitCount++
        }
      }

      if (newCursor < formatted.length) {
        while (newCursor < formatted.length && !/\d/.test(formatted[newCursor])) {
          newCursor++
        }
      }

      return { value: formatted, cursor: newCursor }
    },

    getSearchQuery() {
      // In phone mode, strip non-digits for searching
      if (this.detectedFormat === 'phone') {
        return this.query.replace(/\D/g, '')
      }
      return this.query
    },

    async searchBusinesses() {
      try {
        // Don't clear list or show loading - keep showing previous results until new ones arrive
        this.doneLoading = false

        const searchQuery = this.getSearchQuery()
        const { businesses, isLast } = await coreApi.searchBusinesses(searchQuery)

        // Instant swap to new results
        this.businesses = businesses
        this.doneLoading = isLast
        this.highlightedIndex = 0
      } catch (err) {
        console.error('Failed to search businesses:', err)
      }
    },

    async loadMoreBusinesses() {
      if (this.loading || this.doneLoading) return

      try {
        this.loading = true
        const lastBusiness = this.businesses[this.businesses.length - 1]
        const searchQuery = this.getSearchQuery()
        const { businesses, isLast } = await coreApi.searchBusinesses(searchQuery, lastBusiness.id)

        for (const business of businesses) {
          this.businesses.push(business)
        }
        this.doneLoading = isLast
      } catch (err) {
        console.error('Failed to load more businesses:', err)
      } finally {
        this.loading = false
      }
    },

    handleScroll(event) {
      if (this.doneLoading) return

      const list = event.target
      const maxScroll = list.scrollHeight - list.offsetHeight
      const percentScrolled = list.scrollTop / maxScroll

      if (percentScrolled >= 0.9) {
        this.loadMoreBusinesses()
      }
    },

    selectBusiness(business) {
      this.$emit('update:modelValue', business)
      this.$emit('select', business)
      this.isOpen = false
      this.query = ''
    },

    close() {
      this.isOpen = false
    },

    clearSelection() {
      this.$emit('update:modelValue', null)
      this.$emit('clear')
      this.query = ''
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    },

    // Public method to focus the input
    focus() {
      if (this.selectedBusiness) {
        this.clearSelection()
      } else {
        this.$refs.input?.focus()
      }
    },

    // Public method to get raw query value
    getRawQuery() {
      if (this.detectedFormat === 'phone') {
        return this.query.replace(/\D/g, '')
      }
      return this.query
    },

    handleCreateContact() {
      this.isOpen = false
      // Pass current query value for pre-filling the create modal
      const rawQuery = this.getRawQuery()
      this.$emit('createContact', {
        faxNumber: this.detectedFormat === 'phone' && rawQuery.length >= 3 ? rawQuery : '',
        name: this.detectedFormat !== 'phone' ? this.query : ''
      })
    }
  },
  emits: ['update:modelValue', 'select', 'clear', 'validityChange', 'createContact']
}
</script>

<style scoped>
.business-select-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.business-select-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.business-select-input {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
  cursor: text;
}

.business-select-input:hover:not(.business-select-input--disabled):not(.business-select-input--error) {
  border-color: #9ca3af;
}

.business-select-input--focused:not(.business-select-input--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.business-select-input--disabled {
  background: #f3f4f6;
  opacity: 0.6;
  cursor: not-allowed;
}

.business-select-input--error {
  border-color: #dc2626;
}

.business-select-input--error.business-select-input--focused {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.business-select-input--selected {
  cursor: pointer;
}

.business-select-field {
  width: 100%;
  height: 100%;
  padding: 0 14px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
}

.business-select-field::placeholder {
  color: #9ca3af;
}

.business-select-field:disabled {
  cursor: not-allowed;
}

.business-select-error {
  font-size: 0.8125rem;
  color: #dc2626;
}

/* Selected business display */
.selected-business {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: background 0.15s ease;
}

.selected-business:hover {
  background: #f9fafb;
}

.selected-business .business-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
}

.selected-business .business-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-business .business-fax-number {
  font-weight: 400;
  color: #9ca3af;
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.clear-button:hover {
  background: #f3f4f6;
  color: #374151;
}
</style>

<style>
/* Dropdown styles - not scoped so they apply when teleported */
.business-dropdown {
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  overflow: hidden;
}

.business-dropdown .business-list {
  max-height: min(var(--fd-max-height, 9999px), 240px);
  overflow-y: auto;
}

.business-dropdown .business-item {
  padding: 10px 12px;
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  transition: background 0.1s ease;
}

.business-dropdown .business-item:hover,
.business-dropdown .business-item--highlighted {
  background: var(--color-fill-med, #f3f4f6);
}

.business-dropdown .business-item-icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  flex-shrink: 0;
}

.business-dropdown .business-item-info {
  flex: 1;
  overflow: hidden;
}

.business-dropdown .business-item-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.business-dropdown .business-item-contact {
  font-size: 0.8125rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.business-dropdown .contact-icon {
  width: 14px;
  height: 14px;
}

.business-dropdown .loading-status {
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.business-dropdown .loading-icon {
  height: 24px;
}

.business-dropdown .create-contact-item {
  border-top: 1px solid #e5e7eb;
}

.business-dropdown .create-contact-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--color-fill-med, #f3f4f6);
  color: #666;
  transition: transform 0.15s ease;
}

.business-dropdown .create-contact-item:hover .create-contact-icon {
  transform: scale(1.15);
}

</style>
